import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flag, Undo2, Eye, Copy, Crosshair, Pencil, Check, X as XIcon, Share2 } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import ScorePad, { formatArrow, arrowColor } from "../components/ScorePad";
import { TargetModal, TargetViewer, getFaceConfig } from "../components/TargetFace";
import ChronoButton from "../components/ChronoButton";
import ShareModal from "../components/ShareModal";

function endTotal(end) { return (end || []).reduce((s, v) => s + (v || 0), 0); }
function serieTotal(serie) { return (serie || []).reduce((s, end) => s + endTotal(end), 0); }
function countX(serie, isXSerie) {
  let c = 0;
  (serie || []).forEach((end, ei) => end.forEach((v, ai) => { if (isXSerie?.[ei]?.[ai]) c++; }));
  return c;
}
function countVal(serie, val) {
  let c = 0;
  (serie || []).forEach(end => end.forEach(v => { if (v === val) c++; }));
  return c;
}


// ─── Barre volée en cours ─────────────────────────────────────────────────────
function CurrentEndBar({ end, isXEnd, endNum, serieNum, showSerie }) {
  const filled = (end || []).filter(v => v !== null && v !== undefined);
  const total = filled.reduce((s, v) => s + v, 0);
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 flex items-center gap-3">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 shrink-0">
        {showSerie && <span>S{serieNum} · </span>}Vol. <span className="text-white font-bold">{endNum}</span>
      </div>
      <div className="flex gap-1.5 flex-wrap flex-1">
        {(end || []).map((v, ai) => (
          <div key={ai} className={"h-9 w-9 rounded-md flex items-center justify-center font-heading font-black text-sm " + arrowColor(v, isXEnd?.[ai])}>
            {v !== null && v !== undefined ? formatArrow(v, isXEnd?.[ai]) : <span className="text-zinc-700 text-lg">·</span>}
          </div>
        ))}
      </div>
      {filled.length > 0 && (
        <div className="font-heading font-black text-xl text-cyan-400 tabular-nums shrink-0">{total}</div>
      )}
    </div>
  );
}

// ─── Tableau d'une série ──────────────────────────────────────────────────────
function SerieTable({ serie, isXSerie, serieNum, label }) {
  const total = serieTotal(serie);
  const xs = countX(serie, isXSerie);
  const tens = countVal(serie, 10);
  const nines = countVal(serie, 9);
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-md overflow-hidden">
      <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <span className="font-heading font-black text-2xl text-cyan-400 tabular-nums">{total}</span>
      </div>
      <div className="overflow-x-auto p-4 md:p-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 uppercase text-xs tracking-[0.2em]">
              <th className="text-left pb-3 pr-3">Vol.</th>
              {serie[0].map((_, i) => <th key={i} className="pb-3 px-1">F{i + 1}</th>)}
              <th className="pb-3 px-2">S</th>
            </tr>
          </thead>
          <tbody>
            {serie.map((end, ei) => (
              <tr key={ei} className="border-t border-zinc-800">
                <td className="py-2 pr-3 text-zinc-400 font-heading font-bold">{ei + 1}</td>
                {end.map((v, ai) => (
                  <td key={ai} className="py-2 px-1">
                    <div className={"h-10 w-10 md:h-11 md:w-11 rounded-md flex items-center justify-center font-heading font-black " + arrowColor(v, isXSerie?.[ei]?.[ai])}>
                      {formatArrow(v, isXSerie?.[ei]?.[ai])}
                    </div>
                  </td>
                ))}
                <td className="py-2 px-2 font-heading font-black text-cyan-400 text-lg tabular-nums">{endTotal(end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-zinc-800 px-4 py-3 flex gap-6 text-sm">
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">X</span><span className="font-heading font-bold text-yellow-400">{xs}</span></div>
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">10</span><span className="font-heading font-bold text-white">{tens}</span></div>
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">9</span><span className="font-heading font-bold text-white">{nines}</span></div>
        <div className="ml-auto font-heading font-black text-xl text-cyan-400">{total} pts</div>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function SoloLive() {
  const { soloId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [showTarget, setShowTarget] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/solo/${soloId}`); setS(data); } catch {}
  };

  useEffect(() => { load(); }, [soloId]); // eslint-disable-line

  const scores = useMemo(() => {
    const raw = s?.scores || [];
    if (!raw.length) return [];
    if (Array.isArray(raw[0]?.[0])) return raw;
    return [raw];
  }, [s]);
  const isX = useMemo(() => {
    const raw = s?.is_x || [];
    if (!raw.length) return [];
    if (Array.isArray(raw[0]?.[0])) return raw;
    return [raw];
  }, [s]);
  const numSeries = scores.length;

  const nextSlot = useMemo(() => {
    if (!s) return null;
    for (let ser = 0; ser < scores.length; ser++)
      for (let e = 0; e < scores[ser].length; e++)
        for (let a = 0; a < scores[ser][e].length; a++)
          if (scores[ser][e][a] === null || scores[ser][e][a] === undefined) return [ser, e, a];
    return null;
  }, [s]); // eslint-disable-line

  const grandTotal = useMemo(() => scores.reduce((t, serie) => t + serieTotal(serie), 0), [scores]);
  const grandX = useMemo(() => scores.reduce((t, serie, si) => t + countX(serie, isX[si]), 0), [scores, isX]);
  const grand10 = useMemo(() => scores.reduce((t, serie) => t + countVal(serie, 10), 0), [scores]);
  const grand9 = useMemo(() => scores.reduce((t, serie) => t + countVal(serie, 9), 0), [scores]);
  const hasCoords = useMemo(() => {
    if (!s?.coords?.length) return false;
    const list = Array.isArray(s.coords[0]?.[0]) ? s.coords : [s.coords];
    return list.some(serie => serie.some(end => end.some(c => c && (c.nx !== 0.5 || c.ny !== 0.5))));
  }, [s]);

  const remainingArrows = useMemo(() => {
    if (!nextSlot || !scores.length) return 0;
    const end = scores[nextSlot[0]]?.[nextSlot[1]] || [];
    return end.filter(v => v === null || v === undefined).length;
  }, [nextSlot, scores]);

  const withX = s?.settings?.with_x !== false;
  const targetCfg = getFaceConfig(s?.settings?.face_type || "full", withX);

  const submit = async (value, isXVal) => {
    if (!nextSlot || s.status !== "active") return;
    const [ser, e, a] = nextSlot;
    try {
      const { data } = await api.post(`/solo/${soloId}/score`, { series_index: ser, end_index: e, arrow_index: a, value, is_x: isXVal, nx: 0.5, ny: 0.5 });
      setS(data);
    } catch { toast.error(t("solo_live.submit_error")); }
  };

  const submitFromTarget = async (arrowList) => {
    if (!nextSlot || s.status !== "active") return;
    let current = s;
    for (const a of arrowList) {
      const slot = (() => {
        const sc = current?.scores || [];
        const raw = Array.isArray(sc[0]?.[0]) ? sc : [sc];
        for (let ser = 0; ser < raw.length; ser++)
          for (let e = 0; e < raw[ser].length; e++)
            for (let ax = 0; ax < raw[ser][e].length; ax++)
              if (raw[ser][e][ax] === null || raw[ser][e][ax] === undefined) return [ser, e, ax];
        return null;
      })();
      if (!slot) break;
      try {
        const { data } = await api.post(`/solo/${soloId}/score`, { series_index: slot[0], end_index: slot[1], arrow_index: slot[2], value: a.v, is_x: a.isX, nx: a.nx, ny: a.ny });
        current = data;
      } catch { toast.error(t("solo_live.submit_error")); break; }
    }
    setS(current);
  };

  const undo = async () => {
    if (!s) return;
    let last = null;
    outer: for (let ser = scores.length - 1; ser >= 0; ser--)
      for (let e = scores[ser].length - 1; e >= 0; e--)
        for (let a = scores[ser][e].length - 1; a >= 0; a--)
          if (scores[ser][e][a] !== null && scores[ser][e][a] !== undefined) { last = [ser, e, a]; break outer; }
    if (!last) return;
    try {
      const { data } = await api.post(`/solo/${soloId}/score/reset`, { series_index: last[0], end_index: last[1], arrow_index: last[2] });
      setS(data);
    } catch { toast.error(t("match_live.reset_error")); }
  };

  const finish = async () => {
    try { const { data } = await api.post(`/solo/${soloId}/finish`); setS(data); toast.success(t("solo_live.toast_finished")); } catch {}
  };

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  if (!s) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
      <div className="text-zinc-500 uppercase tracking-widest">{t("common.loading")}</div>
    </div>
  );

  const sessionName = s?.settings?.session_name;

  const startEditName = () => { setNameDraft(sessionName || ""); setEditingName(true); };
  const saveName = async () => {
    try {
      const { data } = await api.patch(`/solo/${soloId}/name`, { session_name: nameDraft.trim() });
      setS((prev) => ({ ...prev, settings: { ...prev.settings, session_name: data.session_name } }));
      setEditingName(false);
    } catch { toast.error(t("solo_live.submit_error")); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      {showTarget && nextSlot && (
        <TargetModal config={targetCfg} maxArrows={remainingArrows} onConfirm={submitFromTarget} onClose={() => setShowTarget(false)} />
      )}
      {showViewer && (
        <TargetViewer config={targetCfg} scores={s.scores} isXData={s.is_x} coords={s.coords} onClose={() => setShowViewer(false)} />
      )}
      {showShare && <ShareModal sessionType="solo" sessionId={soloId} onClose={() => setShowShare(false)} />}

      <header className="sticky top-0 z-20 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="solo-back-live" onClick={() => navigate("/history")} className="p-2 hover:bg-zinc-900 rounded-md">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Solo</div>
              <div className="font-heading font-black text-lg uppercase tracking-tight leading-none">{s.status}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasCoords && (
              <button onClick={() => setShowViewer(true)} className="p-2 hover:bg-zinc-900 rounded-md text-zinc-400 hover:text-cyan-400 transition-colors" title={t("misc.view_impacts")}>
                <Crosshair className="h-5 w-5" />
              </button>
            )}
            <button onClick={() => setShowShare(true)} title={t("share.button")}
              className="p-2 hover:bg-zinc-900 rounded-md text-zinc-400 hover:text-cyan-400 transition-colors">
              <Share2 className="h-5 w-5" />
            </button>
            <ChronoButton variant="header" />
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("solo_live.total")}</div>
              <div data-testid="solo-total" className="font-heading font-black text-3xl text-cyan-400 tabular-nums">{grandTotal}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-900/50 border border-zinc-800 rounded-md">
          {editingName ? (
            <>
              <input autoFocus value={nameDraft} maxLength={80}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                placeholder={t("misc.session_name_ph")}
                className="flex-1 min-w-0 bg-zinc-950 border border-zinc-700 focus:border-cyan-400 outline-none rounded px-3 py-2 text-sm" />
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={saveName} className="p-2 rounded-md text-cyan-400 hover:bg-zinc-800"><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditingName(false)} className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800"><XIcon className="h-4 w-4" /></button>
              </div>
            </>
          ) : (
            <>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("misc.session_name")}</div>
                <div className="font-heading font-black text-xl truncate mt-0.5">{sessionName || t("dashboard.tile_solo_title")}</div>
              </div>
              <button onClick={startEditName} title={t("misc.session_name")}
                className="p-2 rounded-md text-zinc-500 hover:text-cyan-400 hover:bg-zinc-800 transition-colors shrink-0">
                <Pencil className="h-4 w-4" />
              </button>
            </>
          )}
        </div>

        {s.spectator_code && (
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/50 border border-zinc-800 rounded-md">
            <div className="flex items-center gap-2 text-zinc-400">
              <Eye className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.2em]">{t("misc.spectator_code")}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading font-black text-xl tracking-[0.3em] text-cyan-400">{s.spectator_code}</span>
              <button onClick={() => { navigator.clipboard.writeText(s.spectator_code); toast.success(t("misc.code_copied")); }}
                className="p-1.5 rounded-md text-zinc-600 hover:text-white hover:bg-zinc-800 transition-colors">
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {scores.map((serie, si) => (
          <SerieTable key={si} serie={serie} isXSerie={isX[si]} serieNum={si + 1} label={`${t("misc.series")} ${si + 1}`} />
        ))}

        {numSeries > 1 && (
          <div className="bg-zinc-900/80 border border-cyan-400/30 rounded-md px-4 py-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("misc.total_of")} {numSeries} {t("misc.series_plural")}</div>
            <div className="flex items-center gap-6">
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">X</span><span className="font-heading font-bold text-yellow-400">{grandX}</span></div>
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">10</span><span className="font-heading font-bold text-white">{grand10}</span></div>
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">9</span><span className="font-heading font-bold text-white">{grand9}</span></div>
              <div className="ml-auto font-heading font-black text-3xl text-cyan-400 tabular-nums">{grandTotal} pts</div>
            </div>
          </div>
        )}

        {s.status === "active" && nextSlot && (
          <div className="space-y-3">
            <CurrentEndBar
              end={scores[nextSlot[0]]?.[nextSlot[1]]}
              isXEnd={isX[nextSlot[0]]?.[nextSlot[1]]}
              endNum={nextSlot[1] + 1} serieNum={nextSlot[0] + 1} showSerie={numSeries > 1}
            />
            <ScorePad onScore={submit} />
            <div className="flex gap-2">
              <button data-testid="solo-undo-btn" onClick={undo}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border-2 border-zinc-800 hover:border-red-500/50 hover:text-red-400 text-zinc-300 font-bold uppercase tracking-wide px-4 py-3 rounded-md transition-all text-sm">
                <Undo2 className="h-4 w-4" /> {t("match_live.reset_last")}
              </button>
              <button onClick={() => setShowTarget(true)}
                className="inline-flex items-center justify-center gap-2 bg-cyan-400/10 hover:bg-cyan-400/20 border-2 border-cyan-400/60 hover:border-cyan-400 text-cyan-400 font-bold uppercase tracking-wide px-4 py-3 rounded-md transition-all text-sm"
                title={t("misc.place_on_target")}>
                <Crosshair className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {s.status === "active" && !nextSlot && (
          <button data-testid="solo-finish" onClick={finish} className="inline-flex items-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 font-bold uppercase tracking-wide px-6 py-3 rounded-md">
            <Flag className="h-4 w-4" /> {t("solo_live.finish")}
          </button>
        )}

        {s.status === "finished" && (
          <div className="bg-zinc-900/50 border border-cyan-400/30 rounded-md p-6 text-center">
            <div className="text-cyan-400 text-xs uppercase tracking-[0.3em]">{t("solo_live.finished")}</div>
            <div className="mt-2 font-heading font-black text-3xl uppercase">{t("solo_live.final")} {grandTotal}</div>
          </div>
        )}

        {s.status === "active" && (
          <div className="text-right">
            <button data-testid="solo-end-early" onClick={finish} className="text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em]">{t("solo_live.end_early")}</button>
          </div>
        )}
      </main>
    </div>
  );
}